import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';
import {
  ActiveCampaigns,
  DashboardActivity,
  DashboardCharts,
  DashboardMetrics,
  DashboardStats,
} from '@features/dashboard/models/dashboard.model';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private readonly apiUrl = `${environment.apiUrl}`;
  private readonly http = inject(HttpClient);

  /**
   * Handle API errors and transform to user-friendly messages
   */
  private handleApiError(error: unknown): never {
    const httpError = error as HttpErrorResponse;
    const payload = httpError?.error as
      | { message?: string; error?: string }
      | undefined;
    let errorMessage = 'An unexpected error occurred';

    errorMessage =
      payload?.message || payload?.error || httpError?.message || errorMessage;

    throw new Error(errorMessage);
  }

  /**
   * Generic fetch wrapper with error handling
   */
  private async fetchApi<T>(
    endpoint: string,
    options: { method?: string; body?: unknown } = {},
  ): Promise<T> {
    const method = options.method || 'GET';

    try {
      const response = await firstValueFrom(
        this.http.request<T>(method, `${this.apiUrl}${endpoint}`, {
          body: options.body,
        }),
      );

      return (response ?? ({} as T)) as T;
    } catch (error) {
      return this.handleApiError(error);
    }
  }

  /**
   * Get dashboard statistics for an organization
   * GET /organizations/:organizationId/dashboard
   */
  async getDashboardStats(organizationId: string): Promise<DashboardStats> {
    console.log(
      '[DashboardService] Fetching dashboard stats for org:',
      organizationId,
    );

    const stats = await this.fetchApi<DashboardStats>(
      `/organizations/${organizationId}/dashboard`,
    );

    console.log('[DashboardService] Dashboard stats received:', stats);
    return stats;
  }

  /**
   * Get dashboard chart data (30-day timeline + business status distribution)
   * GET /organizations/:organizationId/dashboard/charts
   */
  async getCharts(organizationId: string): Promise<DashboardCharts> {
    return this.fetchApi<DashboardCharts>(
      `/organizations/${organizationId}/dashboard/charts`,
    );
  }

  /**
   * Get the dashboard headline metrics
   * GET /organizations/:organizationId/dashboard/metrics
   */
  async getMetrics(organizationId: string): Promise<DashboardMetrics> {
    return this.fetchApi<DashboardMetrics>(
      `/organizations/${organizationId}/dashboard/metrics`,
    );
  }

  /**
   * Get campaigns in flight with their analysis progress
   * GET /organizations/:organizationId/dashboard/active-campaigns
   */
  async getActiveCampaigns(organizationId: string): Promise<ActiveCampaigns> {
    return this.fetchApi<ActiveCampaigns>(
      `/organizations/${organizationId}/dashboard/active-campaigns`,
    );
  }

  /**
   * Get latest activity (analyses + bulk runs, interleaved)
   * GET /organizations/:organizationId/dashboard/activity
   */
  async getActivity(
    organizationId: string,
    page = 1,
    limit = 20,
  ): Promise<DashboardActivity> {
    return this.fetchApi<DashboardActivity>(
      `/organizations/${organizationId}/dashboard/activity?page=${page}&limit=${limit}`,
    );
  }
}
